import React, { useEffect, useState, useCallback } from 'react'
import { Navigate, useLocation } from 'react-router-dom'

import Loading from '../../components/shared/Loading'
import { Message } from './Message'
import { verifyInviteToken, completeInvite } from '../../../lib/authentication'
import CreatePassword from './CreatePassword'

import { isComplexPassword, createPasswordComplexityList } from '../complexity'
import {
  createIdentityMap,
  getPossibleInviteToken,
  getPasswordPolicyComplexity,
} from '../utils'

import routes from '../../constants/routes'
import Banner from './Banner'
import './auth.scss'

const Stages = createIdentityMap(
  'VERIFYING_INVITE_TOKEN',
  'INVALID_INVITE_TOKEN',
  'ENTERING_NEW_PASSWORD',
  'SUBMITTING_NEW_PASSWORD',
  'VALID_NEW_PASSWORD',
  'INVALID_NEW_PASSWORD',
)

const CompleteInvite = () => {
  const location = useLocation()

  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordComplexity, setPasswordComplexity] = useState({})
  const [username, setUsername] = useState('')
  const [errorState, setErrorState] = useState({ errorSummary: '', errorItems: [] })
  const [stage, setStage] = useState(Stages.VERIFYING_INVITE_TOKEN)

  const inviteToken = getPossibleInviteToken(location)

  const isOnStage = (...stages) => stages.includes(stage)

  const setError = (errorSummary = '', ...errorItems) => {
    setErrorState({ errorSummary, errorItems })
  }

  const handleVerifyInviteToken = useCallback(async () => {
    setStage(Stages.VERIFYING_INVITE_TOKEN)

    try {
      const { username: usernameFromToken, policy } = await verifyInviteToken(inviteToken)
      const isVerified = !!policy

      if (isVerified) {
        setPasswordComplexity(getPasswordPolicyComplexity(policy))
      }

      setUsername(usernameFromToken || '')
      setStage(isVerified ? Stages.ENTERING_NEW_PASSWORD : Stages.INVALID_INVITE_TOKEN)
    } catch (err) {
      console.error(err)
      setStage(Stages.INVALID_INVITE_TOKEN)
    }
  }, [inviteToken])

  useEffect(() => {
    handleVerifyInviteToken()
  }, [handleVerifyInviteToken])

  const complexityList =  createPasswordComplexityList(passwordComplexity, newPassword, username)

  const handleChange = (event) => {
    const { id, value } = event.target

    if (id === 'newPassword') setNewPassword(value)
    if (id === 'confirmPassword') setConfirmPassword(value)
  }

  const handleSubmitForm = async (event) => {
    event.preventDefault()

    setError()
    setStage(Stages.SUBMITTING_NEW_PASSWORD)

    const { success, error: apiError } = await completeInvite(
      inviteToken,
      newPassword,
    )

    if (!success && apiError) {
      const { errorSummary = '', errorItems = [] } = apiError

      setError(errorSummary, ...errorItems)
      setStage(Stages.INVALID_NEW_PASSWORD)

      return
    }

    if (success && !apiError) {
      setStage(Stages.VALID_NEW_PASSWORD)

      return
    }

    setError('Unknown Error occurred, please try again')
    setStage(Stages.ENTERING_NEW_PASSWORD)
  }

  const validateForm = () => {
    const isPasswordMatch = newPassword === confirmPassword

    return isComplexPassword(passwordComplexity, newPassword, username) && isPasswordMatch
  }

  const renderErrors = () => {
    const { errorSummary = 'Unknown Error Occurred', errorItems = [] } = errorState

    return (
      <Message title={errorSummary} type="danger">
        {errorItems.map((errorItem) => (
          <p key={window.btoa(errorItem)}>{errorItem}</p>
        ))}
      </Message>
    )
  }

  if (isOnStage(Stages.INVALID_INVITE_TOKEN)) {
    return (
      <Navigate
        to="/login"
        state={{ from: 'inviteToken' }}
        replace
      />
    )
  }

  if (isOnStage(Stages.VALID_NEW_PASSWORD)) {
    return <Navigate to={routes.SUBMISSIONS} replace />
  }

  return (
    <div className="AuthContainer vertical">
      <Banner username={username} />

      {isOnStage(Stages.VERIFYING_INVITE_TOKEN, Stages.SUBMITTING_NEW_PASSWORD) && (
        <Loading classes="loading" />
      )}

      {isOnStage(Stages.ENTERING_NEW_PASSWORD, Stages.INVALID_NEW_PASSWORD) && (
        <CreatePassword
          title="Choose a New Password"
          onSubmit={handleSubmitForm}
          onChange={handleChange}
          validateForm={validateForm}
          complexityList={complexityList}
          newPassword={newPassword}
          confirmPassword={confirmPassword}
        >
          {isOnStage(Stages.INVALID_NEW_PASSWORD) && renderErrors()}
        </CreatePassword>
      )}
    </div>
  )
}

export default CompleteInvite
